import {
  computed,
  inject,
  Injectable,
  INJECTOR,
  linkedSignal,
  resource,
  ResourceRef,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { Account } from '@softapps/wws/core';
import {
  Address,
  Cart,
  CartItem,
  Discount,
  DocumentType,
  FinalizeResult,
  Price,
  ReferenceDocument,
  Unit,
  UpdateCartItem,
} from '@softapps/wws/pos-core';
import {
  AudioService,
  handleRequestError,
  requestErrorMessageFactory,
  Scan,
  ScannerStore,
  SOFTLINE_FEATURE_SCANNER,
} from '@softline/application';
import { interpolate, isDefinedNotEmpty, Store } from '@softline/core';
import {
  askDeleteFn,
  chooseFn,
  confirmFn,
  MessageBarService,
  TranslationService,
} from '@softline/ui-core';
import {
  catchError,
  concatMap,
  defer,
  distinctUntilChanged,
  filter,
  finalize,
  from,
  map,
  Observable,
  of,
  repeat,
  skip,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs';
import { CartServiceErrors } from '../errors/cart-service.errors';
import { CartRepository } from '../repositories/cart.repository';
import { CreditLimitResponse } from '@softapps/wws/pos-core';
import { Process } from '@softapps/wws/pos-core';
import { isCartEqual } from '../utils/is-cart-equal';
import { isCustomerCard } from '../utils/is-customer-card';

export const DISTRIBUTION_TYPES = [
  {
    id: 1,
    name: 'Selbstabholung',
    value: 'Selbstabholung',
    label: 'Selbstabholung',
    icon: 'fa-regular fa-hand text-3xl text-primary',
  },
  {
    id: 2,
    name: 'Lieferung',
    value: 'Lieferung',
    label: 'Lieferung',
    icon: 'fa-regular fa-truck text-3xl text-primary',
  },
];

@Injectable()
export class CartService {
  readonly #cartRepository = inject(CartRepository);
  readonly #messageBar = inject(MessageBarService);
  readonly #choose = chooseFn();
  readonly #askDelete = askDeleteFn();
  readonly injector = inject(INJECTOR);

  readonly #processesResource = resource({
    params: () => ({}),
    loader: async () => {
      return this.#cartRepository.loadProcesses();
    },
  });

  readonly #templateCart = resource({
    loader: () => this.#cartRepository.loadTemplateCart(),
  });

  readonly #cartResource: ResourceRef<Cart | null | undefined> = resource({
    params: () => this.#selectedProcess(),
    loader: async (params) => {
      if (!params.params) {
        return null;
      }

      const cart = await this.#cartRepository.loadCart(params.params.id);
      //if ((!params.previous || params.previous.status === ResourceStatus.Idle) && cart)
      this.selectedItemId.set(cart?.items[cart?.items.length - 1]?.id ?? 0);
      return cart;
    },
  });

  readonly #selectedProcess = linkedSignal<Process | null>(() => null);
  readonly openConfirmModal = confirmFn();
  readonly errorState = signal(false);

  // Reading `.value()` on an errored resource THROWS — the throw propagates into every
  // consumer/effect and re-runs forever. Gate on hasValue() and surface the error.
  readonly #cart = linkedSignal<Cart | null>(() => {
    return this.#cartResource.hasValue() ? (this.#cartResource.value() ?? null) : null;
  });

  readonly selectedItemId = signal<number | null>(null);

  // Single source of truth for "an item was just added — scroll the cart to it".
  // EVERY add path (search modal, products modal, side nav, scan, return) sets
  // this so the page has one thing to react to. `seq` bumps on every add so that
  // re-adding the SAME line (quantity merge) still fires the reaction.
  readonly lastAddedItem = signal<{ id: number; seq: number } | null>(null);
  #addSeq = 0;

  #markItemAdded(id: number | null | undefined): void {
    if (id == null) return;
    this.lastAddedItem.set({ id, seq: ++this.#addSeq });
  }

  // Pinpoint the line an add actually affected. The frontend "neue Position"
  // toggle decides whether to merge: off => the add merges into an existing line
  // (quantity goes up, id stays), on (forceNewItem) => a brand-new line is added.
  // Priority: a genuinely new line, then the line whose quantity increased most,
  // then any line for the product, then the last line.
  #findAddedLine(
    items: CartItem[],
    productId: number,
    beforeQuantities: Map<number, number>
  ): CartItem | undefined {
    const newLine = items.find((i) => !beforeQuantities.has(i.id));
    if (newLine) return newLine;

    let increased: CartItem | undefined;
    let maxDelta = 0;
    for (const item of items) {
      const delta = (item.quantity ?? 0) - (beforeQuantities.get(item.id) ?? 0);
      if (delta > maxDelta) {
        maxDelta = delta;
        increased = item;
      }
    }
    if (increased) return increased;

    return items.find((i) => i.product?.id === productId) ?? items[items.length - 1];
  }

  readonly #selectedItem = linkedSignal<CartItem | null>(() => {
    const items = this.#cart()?.items ?? [];

    return items.find((i) => i.id === this.selectedItemId()) ?? null;
  });

  readonly #newProcess = signal<Process | null>(null);
  readonly #newPosition = signal<boolean>(false);

  /* PUBLIC API */
  readonly processes = computed(() =>
    this.#processesResource.hasValue() ? (this.#processesResource.value() ?? []) : []
  );
  readonly processesLoading = computed(() => this.#processesResource.isLoading());
  readonly cartError = computed(() => this.#cartResource.status() === 'error');
  readonly processesError = computed(() => this.#processesResource.status() === 'error');
  readonly selectedProcess = this.#selectedProcess.asReadonly();

  readonly cart = this.#cart.asReadonly();

  readonly cartLoading = computed(() => this.#cartResource.isLoading());
  readonly canCreateProcess = computed(() => this.#newProcess() === null);
  readonly selectedItem = computed(() => this.#selectedItem() ?? null);
  readonly newPosition = this.#newPosition.asReadonly();

  readonly processesLoaded = computed(() => {
    const status = this.#processesResource.status();
    return status === 'resolved' || status === 'local';
  });

  readonly previousCart = linkedSignal<Cart | null, Cart | null>({
    source: this.#cart,
    computation: (_, previous) => {
      return previous?.source ?? null;
    },
  });

  readonly templateCartResource = this.#templateCart.asReadonly();
  readonly templateCart = computed(() =>
    this.#templateCart.hasValue() ? (this.#templateCart.value() ?? null) : null
  );

  private readonly addItem$ = new Subject<{
    productId: number;
    quantity: number;
    newPosition: boolean;
  }>();

  /** emits the cart item that was just added via addItem() */
  readonly itemAdded$ = new Subject<CartItem>();

  readonly selectedProcess$ = toObservable<Process | undefined | null>(
    this.#selectedProcess
  );

  readonly #store = inject(Store);
  readonly #audioService = inject(AudioService);

  readonly scanning = signal(false);

  readonly scanDisabled = linkedSignal(() => {
    return this.cart()?.documentType === 'return_receipt';
  });

  readonly #currentRouterUrl$ = inject(Router).events.pipe(
    filter((event) => event instanceof NavigationEnd || event instanceof NavigationStart),
    map((event) =>
      event instanceof NavigationEnd ? event.urlAfterRedirects : event.url
    ),
    map((url) => url ?? '')
  );

  readonly #isInvalidUrl$ = this.#currentRouterUrl$.pipe(
    map((url) => {
      return url.includes('/payment') || !url.includes('/pos');
    }),
    distinctUntilChanged(),
    takeUntilDestroyed()
  );

  readonly #isValidUrl$ = this.#isInvalidUrl$.pipe(
    map((invalid) => !invalid),
    distinctUntilChanged(),
    takeUntilDestroyed()
  );

  readonly itemAddInProgress = signal(false);
  readonly addingProductId = signal<number | null>(null);

  /** Cart/item mutations (update, delete, finalize, ...) currently in flight. */
  readonly #pendingMutations = signal(0);
  /** `true` while any add, scan or mutation request is running — lock the pay actions meanwhile. */
  readonly updating = computed(
    () => this.#pendingMutations() > 0 || this.itemAddInProgress() || this.scanning()
  );

  #track<T>(request: Promise<T>): Promise<T> {
    this.#pendingMutations.update((count) => count + 1);
    return request.finally(() =>
      this.#pendingMutations.update((count) => Math.max(0, count - 1))
    );
  }

  #trackStream<T>(source: Observable<T>): Observable<T> {
    return defer(() => {
      this.#pendingMutations.update((count) => count + 1);
      return source.pipe(
        finalize(() => this.#pendingMutations.update((count) => Math.max(0, count - 1)))
      );
    });
  }

  constructor() {
    const injector = inject(INJECTOR);

    // add item stream
    this.addItem$
      .pipe(
        concatMap((addItemRequestParams) => {
          this.itemAddInProgress.set(true);
          this.addingProductId.set(addItemRequestParams.productId);
          const beforeQuantities = new Map<number, number>(
            (this.#cart()?.items ?? []).map((i) => [i.id, i.quantity ?? 0])
          );
          // use selected process otherwise create a new process
          return this.getOrCreateCartProcess$().pipe(
            concatMap((selectedProcess) => {
              return this.#cartRepository
                .addItem$(
                  selectedProcess.id,
                  addItemRequestParams.productId,
                  addItemRequestParams.quantity,
                  addItemRequestParams.newPosition
                )
                .pipe(
                  map((newCart) => ({
                    newCart,
                    productId: addItemRequestParams.productId,
                    beforeQuantities,
                  })),
                  tap({
                    subscribe: () => this.itemAddInProgress.set(true),
                    finalize: () => {
                      this.itemAddInProgress.set(false);
                      this.addingProductId.set(null);
                    },
                  }),
                  catchError((e) => {
                    this.itemAddInProgress.set(false);
                    handleRequestError(e, injector, { show: true });
                    return of(null);
                  })
                );
            }),
            // getOrCreateCartProcess$ failed, so the inner pipe's finalize never ran.
            catchError((e) => {
              this.itemAddInProgress.set(false);
              this.addingProductId.set(null);
              handleRequestError(e, injector, { show: true });
              return of(null);
            })
          );
        }),
        takeUntilDestroyed()
      )
      .subscribe((result) => {
        if (!result?.newCart) return;
        const { newCart, productId, beforeQuantities } = result;
        this.itemAddInProgress.set(false);
        this.#cart.set(newCart);
        const addedItem = this.#findAddedLine(newCart.items, productId, beforeQuantities);
        this.selectedItemId.set(addedItem?.id ?? null);
        this.#markItemAdded(addedItem?.id);
        this.#newProcess.set(null);
        this.setNewPosition(false);
        if (addedItem) this.itemAdded$.next(addedItem);
      });

    this.#store
      .observe(SOFTLINE_FEATURE_SCANNER, ScannerStore.getters.latest)
      .pipe(
        skip(1),
        withLatestFrom(this.#isValidUrl$),
        filter(([scan, isValidUrl]) => !!scan && isValidUrl),
        map(([scan]) => scan),
        concatMap((scan) => {
          if (!scan || this.scanDisabled()) {
            return of(null);
          } else if (this.errorState()) {
            this.#audioService.play('warning');
            return of(null);
          }

          this.scanning.set(true);

          // TODO: strip the 9C0KK prefix server-side — the BÖNI app's KSK reader
          // prepends it and the scan endpoint rejects the code while it is attached
          if (scan.data.startsWith('9C0KK')) {
            scan.data = scan.data.slice(3);
          }

          if (isCustomerCard(scan.data, scan.labelType)) {
            return this.scanCustomerCard$({
              ...scan,
              data: scan.data,
            }).pipe(
              finalize(() => this.scanning.set(false)),
              catchError((error) => {
                handleRequestError(error, injector, { show: true });
                this.scanning.set(false);
                this.#audioService.play('warning');
                return of(null);
              })
            );
          }

          return this.scanItem$(scan, 1).pipe(
            finalize(() => this.scanning.set(false)),
            catchError((error) => {
              this.showBlockingScanError(error);
              // handleRequestError(error, injector, { show: true });
              this.scanning.set(false);
              this.#audioService.play('warning');
              return of(null);
            })
          );
        }),
        takeUntil(
          this.#isInvalidUrl$.pipe(
            skip(1),
            filter((o) => o === true)
          )
        ),
        repeat({
          delay: () =>
            this.#isValidUrl$.pipe(
              skip(1),
              filter((o) => o === true)
            ),
        }),
        takeUntilDestroyed()
      )
      .subscribe(async (cart) => {
        if (cart) {
          await this.setCart(cart);
          const addedItem = cart.items[cart.items.length - 1];
          this.selectItem(addedItem);
          this.#markItemAdded(addedItem?.id);
        }
      });
  }

  getNewPosition(): boolean {
    return this.newPosition();
  }

  setNewPosition(value: boolean): void {
    this.#newPosition.set(value);
  }

  async updateReason(reason: string): Promise<void> {
    const cartId = this.cart()?.id;
    if (!cartId) return;
    const result = await this.#track(
      this.#cartRepository.updateCart(cartId, {
        note: {
          note: reason,
          categoryId: null,
        },
      })
    );

    await this.setCart(result);
  }

  async updateZipCode(zipcode: string): Promise<Cart | null> {
    const cartId = this.cart()?.id;
    if (!cartId) return null;

    let result: Cart | null = null;

    try {
      result = await this.#track(
        this.#cartRepository.updateCart(cartId, {
          zipcode,
        })
      );

      await this.setCart(result);
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
    }

    return result;
  }

  async updateCostCenter(value: Cart['costCenter'] | null): Promise<Cart | null> {
    const cartId = this.cart()?.id;
    if (!cartId) return null;

    const result = await this.#track(
      this.#cartRepository.updateCart(cartId, {
        costCenter: value ?? null,
      })
    );

    await this.setCart(result);
    return result;
  }

  private getOrCreateCartProcess$(): Observable<Process> {
    return this.selectedProcess$.pipe(
      take(1),
      concatMap((process) => {
        if (!process) return defer(() => from(this.addProcess()));
        return of(process);
      })
    );
  }

  private showBlockingScanError(error: any): void {
    this.errorState.set(true);

    const message = requestErrorMessageFactory(error);

    const translator = this.injector.get(TranslationService);
    const subject = message.subject
      ? interpolate(translator.translate(message.subject)(), message.data as any)
      : null;
    const text = interpolate(
      translator.translate(message.message)(),
      message.data as any
    );

    this.openConfirmModal({
      dismiss: false,
      title: 'Fehler beim Scannen',
      subtitle: subject ?? text,
      contentText: 'Bitte bestätigen Sie diesen Dialog um weiter scannen zu können',
      dismissOnNavigation: true,
    }).then(() => {
      this.errorState.set(false);
    });
  }

  async getOrCreateCartProcess(): Promise<Process> {
    let process = this.selectedProcess();

    if (!process) {
      process = await this.addProcess();
    }

    return process;
  }

  async addProcess(): Promise<Process> {
    const createdProcess = await this.#track(this.#cartRepository.createProcess());
    this.#processesResource.set([...this.processes(), createdProcess]);
    this.#newProcess.set(createdProcess);
    this.selectProcess(createdProcess);
    return createdProcess;
  }

  selectProcess(process: Process | null) {
    this.#selectedProcess.set(process);
  }

  async setCart(value: Cart | null): Promise<void> {
    const templateCart = untracked(() =>
      this.#templateCart.hasValue() ? this.#templateCart.value() : null
    );
    const isEmpty =
      (value?.items?.length ?? 0) < 1 && (value?.cashbookEntries?.length ?? 0) < 1;
    if (!isEmpty) {
      this.#cart.set(value);
    } else if (isCartEqual(value, templateCart ?? null)) {
      const process = untracked(() => this.processes().find((o) => o.id === value?.id));
      try {
        if (process) await this.removeProcess(process);
        this.#cart.set(null);
        this.selectProcess(null); // switch to the empty template process
      } catch (e) {
        handleRequestError(e, this.injector, { show: false });
        this.#cart.set(value);
      }
    } else {
      this.#cart.set(value);
    }
  }

  reload() {
    this.#cartResource.reload();
  }

  async removeProcess(process: Process, force = false) {
    if (this.processes().length >= 1 || force) {
      await this.#track(this.#cartRepository.removeProcess(process));
      this.#processesResource.set(this.processes().filter((p) => p.id !== process.id));
      this.#newProcess.set(null);
    }
  }

  async tryRemoveAllProcesses(): Promise<number> {
    try {
      await this.#track(this.#cartRepository.removeEmptyCarts());
      return this.processes().length ?? 0;
    } catch {
      return this.processes().length ?? 0;
    }
  }

  async changeDocumentHeader(documentHeader: string): Promise<void> {
    const cart = await this.getOrCreateCartProcess();
    const updatedCart = await this.#track(
      this.#cartRepository.updateCart(cart.id, {
        documentHeader,
      })
    );
    await this.setCart(updatedCart);
  }

  async changeDocumentFooter(documentFooter: string): Promise<void> {
    const cart = await this.getOrCreateCartProcess();

    const updatedCart = await this.#track(
      this.#cartRepository.updateCart(cart.id, {
        documentFooter,
      })
    );
    await this.setCart(updatedCart);
  }

  async updateCollector(collector: string | null): Promise<void> {
    const cart = await this.getOrCreateCartProcess();
    if (!cart?.id) return;
    const updatedCart = await this.#track(
      this.#cartRepository.updateCart(cart.id, {
        collector,
      })
    );
    await this.setCart(updatedCart);
  }

  async ensureCollectorForCart(cartId: number): Promise<string | null> {
    const cart = await this.#cartRepository.loadCart(cartId);
    const existing = cart?.collector?.trim();
    if (existing) return existing;
    const collector = cart?.account?.name?.trim();
    if (!collector) return null;
    const updatedCart = await this.#track(
      this.#cartRepository.updateCart(cartId, {
        collector,
      })
    );
    if (updatedCart?.id === this.cart()?.id) {
      await this.setCart(updatedCart);
    }
    return updatedCart?.collector ?? collector;
  }

  async updateSellOffType(type: string): Promise<void> {
    const cart = await this.getOrCreateCartProcess();
    if (!cart?.id) return;
    const updatedCart = await this.#track(
      this.#cartRepository.updateCart(cart.id, {
        sellOffType: type,
      })
    );
    await this.setCart(updatedCart);
  }

  async updatePriceZero(key: string, indicator: string): Promise<void> {
    const cart = this.#cart();
    if (!cart || !cart?.id) return;
    const updatedCart = await this.#track(
      this.#cartRepository.updateItem(cart.id, key, {
        indicator,
      })
    );
    await this.setCart(updatedCart);
  }

  async updateBonuspoints(value: boolean): Promise<void> {
    const cart = this.#cart();
    if (!cart || !cart?.id) return;

    this.#cart.update((cart) => (cart ? { ...cart, bonuspointsAvailable: value } : null));

    const updatedCart = await this.#track(
      this.#cartRepository.updateCart(cart.id, {
        bonuspointsAvailable: value,
      })
    );

    await this.setCart(updatedCart);
  }

  async updateDeliveryAddress(address: string | null): Promise<void> {
    const cart = await this.getOrCreateCartProcess();
    const updatedCart = await this.#track(
      this.#cartRepository.updateCart(cart.id, {
        deliveryAddress: address,
      })
    );
    await this.setCart(updatedCart);
  }

  async updateManualDeliveryAddress(address: Address): Promise<void> {
    const cart = await this.getOrCreateCartProcess();

    const updatedCart = await this.#track(
      this.#cartRepository.updateCart(cart.id, {
        address: address,
      })
    );
    await this.setCart(updatedCart);
  }

  async updateProject(project: Cart['project']): Promise<Cart | null> {
    const cart = await this.getOrCreateCartProcess();

    const updatedCart = await this.#track(
      this.#cartRepository.updateCart(cart.id, {
        project,
      })
    );
    await this.setCart(updatedCart);
    return updatedCart;
  }

  setDistributionType(distributionType: string): void {
    const cart = this.#cart();
    if (!cart) return;
    const newDistributionType = DISTRIBUTION_TYPES.find(
      (type) => type.value === distributionType
    );
    if (!newDistributionType) return;
    this.#cart.set({
      ...cart,
      distributionType: newDistributionType,
    });
  }

  async setCustomer(customer: Account | null | undefined): Promise<void> {
    const cart = await this.getOrCreateCartProcess();

    if (!customer?.id) return;

    const body = { accountId: customer?.id };

    const result = await this.validateCreditLimit(body);
    if (!result?.continue) return;

    const updatedCart = await this.#track(this.#cartRepository.updateCart(cart.id, body));
    await this.setCart(updatedCart);
  }

  async setCustomConstructionSite(
    customConstructionSite: string | null | undefined
  ): Promise<void> {
    const cart = await this.getOrCreateCartProcess();

    const body = {
      customConstructionside: customConstructionSite, // Note: The property name is intentionally kept as 'customConstructionside' to match the backend API expectations.
    };

    const updatedCart = await this.#track(this.#cartRepository.updateCart(cart.id, body));
    await this.setCart(updatedCart);
  }

  async setConstructionSite(accountId: number | null | undefined): Promise<void> {
    const cart = await this.getOrCreateCartProcess();

    const updatedCart = await this.#track(
      this.#cartRepository.updateCart(cart.id, {
        accountId,
      })
    );

    await this.setCart(updatedCart);
  }

  async setDiscount(discount: number): Promise<void> {
    if (discount < 0 || discount > 100) {
      throw CartServiceErrors.discountNotAllowed();
    }

    const cart = await this.getOrCreateCartProcess();

    const updatedCart = await this.#track(
      this.#cartRepository.updateCart(cart.id, {
        generalDiscount: discount,
      })
    );

    await this.setCart(updatedCart);
  }

  async loadDocumentTypes(): Promise<string[]> {
    const cart = this.#cart();

    try {
      const docTypes = await this.#cartRepository.loadDocumentTypes(cart?.id);
      return docTypes;
    } catch (e) {
      console.error(e);
      return [];
    }
  }

  async reloadProcesses(): Promise<Process[]> {
    const processes = await this.#cartRepository.loadProcesses();
    this.#processesResource.set(processes);
    return processes;
  }

  clearProcesses() {
    this.#processesResource.set([]);
  }

  async setDocumentType(
    newDocumentType: 'return_receipt',
    referenceDocument: ReferenceDocument
  ): Promise<Cart | null>;
  async setDocumentType(
    newDocumentType: Exclude<DocumentType, 'return_receipt'>
  ): Promise<Cart | null>;
  async setDocumentType(
    newDocumentType: DocumentType,
    referenceDocument?: ReferenceDocument
  ): Promise<Cart | null> {
    const cart = await this.getOrCreateCartProcess();

    const creditLimitResult = await this.validateCreditLimit({
      documentType: newDocumentType,
    });

    if (!creditLimitResult?.continue) return null;

    const updateCartPayload = {
      documentType: newDocumentType,
    };

    if (referenceDocument) {
      updateCartPayload['referenceDocument'] = referenceDocument;
    }

    const updatedCart = await this.#track(
      this.#cartRepository.updateCart(cart.id, updateCartPayload)
    );

    await this.setCart(updatedCart);
    return updatedCart;
  }

  async addItem(
    productId: number,
    newPosition: boolean,
    quantity?: number
  ): Promise<void> {
    this.addItem$.next({
      productId,
      newPosition,
      quantity: quantity !== undefined ? quantity : 1,
    });
  }

  selectItem(id: number);
  selectItem(item: CartItem | null);
  selectItem(itemOrId: CartItem | null | number) {
    if (itemOrId === null || itemOrId === undefined) {
      this.#selectedItem.set(null);
      this.selectedItemId.set(null);
      return;
    }

    const currentCart = this.#cart();

    if (!currentCart) {
      this.#selectedItem.set(null);
      this.selectedItemId.set(null);
      return;
    }

    const itemId = typeof itemOrId === 'number' ? itemOrId : itemOrId.id;
    const cartItem = currentCart?.items.find((i) => i.id === itemId) ?? null;

    this.selectedItemId.set(cartItem?.id ?? null);
  }

  async deleteItem(key: string) {
    const cartId = this.#cart()?.id;
    const cart = this.#cart();
    if (!cartId || !cart) return;

    if (cart.documentType === 'return_receipt' && cart.items.length === 1) {
      const result = await this.#askDelete({
        title: 'Letzter Artikel - Retoure abbrechen',
        question:
          'Wollen Sie den Artikel löschen? Da es der letzte Artikel ist, wird damit der ganze Retourbeleg gelöscht.',
      });
      if (result === 'DISMISSED' || result === 'NO') return;
      if (result === 'YES') return await this.cancelCart();
    }

    const newCart = await this.#track(this.#cartRepository.deleteItem(key, cartId));
    if (!newCart) return;

    await this.setCart(newCart);
  }

  async updatePrice(key: string, newPrice: Price | Partial<Price>): Promise<void> {
    try {
      const updatedCart = await this.updateCartItem(key, () => ({
        price: newPrice,
      }));

      if (!updatedCart) return;
      await this.setCart(updatedCart);
    } catch (e) {
      throw CartServiceErrors.updateCartItemFailed(e);
    }
  }

  async updateUnit(key: string, newUnit: Unit): Promise<void> {
    try {
      const updatedCart = await this.updateCartItem(key, () => {
        return {
          unit: newUnit,
        };
      });
      if (!updatedCart) return;
      await this.setCart(updatedCart);
    } catch (e) {
      throw CartServiceErrors.updateCartItemFailed(e);
    }
  }

  async updateDiscount(key: string, newDiscounts: Discount): Promise<void> {
    if (newDiscounts === null || newDiscounts === undefined) {
      const updatedCart = await this.updateCartItem(key, (cartItem) => {
        return {
          price: {
            ...cartItem.price,
            discounts: cartItem?.price?.discounts,
          },
        };
      });
      if (!updatedCart) return;
      await this.setCart(updatedCart);
      return;
    }

    const updatedCart = await this.updateCartItem(key, (cartItem) => {
      return {
        price: {
          ...cartItem.price,
          discounts: [...(cartItem?.price?.discounts ?? []), newDiscounts],
        },
      };
    });

    if (!updatedCart) return;
    await this.setCart(updatedCart);
  }

  async updateDiverseDescriptions(
    key: string,
    changes: {
      description1: string;
      description2: string | null;
      description3: string | null;
    }
  ): Promise<void> {
    const updatedCart = await this.updateCartItem(key, () => {
      const update: UpdateCartItem = {
        manualProductDescription: {
          description1: changes.description1,
          description2: changes.description2,
          description3: changes.description3,
        },
      };
      return update;
    });

    if (!updatedCart) return;
    await this.setCart(updatedCart);
  }

  async updateProductDescription(key: string, newDescription: string): Promise<void> {
    const updatedCart = await this.updateCartItem(key, () => {
      return {
        description: newDescription,
      } as any;
    });

    if (!updatedCart) return;
    await this.setCart(updatedCart);
  }

  async updateQuantity(
    key: string,
    newQuantity: number,
    unitCompositionId?: number | null
  ): Promise<void> {
    if (newQuantity === 0) {
      // await this.deleteItem(key);
    }

    try {
      const updatedCart = await this.updateCartItem(key, () => {
        return {
          quantity: newQuantity,
          unitCompositionId: unitCompositionId ?? undefined,
        };
      });
      if (!updatedCart) return;
      await this.setCart(updatedCart);
    } catch (e) {
      throw CartServiceErrors.updateCartItemFailed(e);
    }
  }

  updateQuantity$(
    key: string,
    newQuantity: number,
    unitCompositionId?: number | null
  ): Observable<Cart | null> {
    const cartId = this.cart()?.id;
    const cartItem = this.getCartItem(key);
    if (!cartItem || !cartId) return of(null);

    return this.#trackStream(
      this.#cartRepository.updateItem$(cartId, key, {
        key: cartItem.key,
        quantity: newQuantity,
        unitCompositionId: unitCompositionId ?? cartItem?.unitCompositionId ?? undefined,
      })
    );
  }

  async changePickUpStatus(key: string, value?: string): Promise<void> {
    const currentCart = this.#cart()?.items.find((i) => i.key === key);

    try {
      const updatedCart = await this.updateCartItem(key, () => {
        return {
          takeawayMark: value
            ? value
            : currentCart?.takeawayMark?.value === 'J'
              ? 'N'
              : 'J',
        };
      });
      if (!updatedCart) return;
      await this.setCart(updatedCart);
    } catch (e) {
      throw CartServiceErrors.updateCartItemFailed(e);
    }
  }

  async scanItem(scan: Scan, quantity: number): Promise<void> {
    const selectedProcess = await this.getOrCreateCartProcess();
    if (!selectedProcess) return;

    const forceNewItem = this.newPosition();

    const newCart = await this.#track(
      this.#cartRepository.scanItem(selectedProcess.id, scan, quantity, forceNewItem)
    );

    if (!newCart) return;

    if (forceNewItem) this.setNewPosition(false);

    await this.setCart(newCart);
    this.#newProcess.set(null);
    // TODO: have scanItem return the affected line — assuming the last item is wrong
    // whenever the backend merges the scan into an existing position
    const addedItem = newCart.items[newCart.items.length - 1];
    this.selectItem(addedItem);
    this.#markItemAdded(addedItem?.id);
  }

  scanItem$(scan: Scan, quantity: number): Observable<Cart | null> {
    return this.getOrCreateCartProcess$().pipe(
      concatMap((selectedProcess) => {
        const forceNewItem = this.newPosition();

        const response = this.#cartRepository.scanItem$(
          selectedProcess.id,
          scan,
          quantity,
          forceNewItem
        );

        if (forceNewItem) this.setNewPosition(false);

        return response;
      })
    );
  }

  scanCustomerCard$(scan: Scan): Observable<Cart | null> {
    return this.getOrCreateCartProcess$().pipe(
      concatMap((process) => {
        if (scan.data.startsWith('8011')) {
          return defer(() =>
            from(
              this.#track(
                this.#cartRepository.updateCart(process.id, {
                  customerCardNumber: scan.data,
                })
              )
            )
          ) as Observable<Cart | null>;
        }

        return this.#cartRepository.scanCustomerCard$(scan).pipe(
          switchMap((result) => {
            const changes: Partial<Cart | null> = {};

            if (isDefinedNotEmpty(result?.customerCardNumber)) {
              if (result?.customerCardNumber?.toLowerCase()?.startsWith('kk_')) {
                changes['customerCardNumber'] = result.customerCardNumber.slice(3);
              } else if (result?.customerCardNumber?.toLowerCase()?.startsWith('kto_')) {
                changes['customerCardNumber'] = result.customerCardNumber.slice(4);
              } else {
                changes['customerCardNumber'] = result?.customerCardNumber;
              }
            } else if (isDefinedNotEmpty(result?.customerNumber))
              changes['accountId'] = result?.customerNumber;

            if (isDefinedNotEmpty(changes))
              return defer(() =>
                from(this.#track(this.#cartRepository.updateCart(process.id, changes)))
              ) as Observable<Cart | null>;
            else return of(null) as Observable<Cart | null>;
          })
        );
      })
    );
  }

  async checkConditionReasonNecessary(): Promise<boolean> {
    const cartId = this.cart()?.id;
    if (!cartId) return false;
    return this.#cartRepository.checkConditionReasonNecessary(cartId);
  }

  async validateCreditLimit(
    body: { documentType: string } | { accountId: number } | undefined
  ): Promise<{
    continue: boolean;
    response?: CreditLimitResponse | null;
  }> {
    const cartId = this.cart()?.id;

    if (!cartId)
      return {
        continue: true,
      };

    const creditLimit = await this.#cartRepository.checkCreditLimit(cartId, body ?? {});

    if (!creditLimit.exceeded)
      return {
        continue: true,
      };

    let canContinue = true;

    if (creditLimit?.error) {
      this.#messageBar.error(creditLimit.error?.message ?? '', creditLimit.error?.title);
      canContinue = false;
    } else if (creditLimit?.warning) {
      this.#messageBar.warning(
        creditLimit.warning?.message ?? '',
        creditLimit.warning?.title
      );
    } else if (creditLimit.showExceededDialog) {
      const chooseResult = await this.#choose({
        icon: 'fa-regular fa-triangle-exclamation',
        title: 'Kreditlimit überschritten',
        subtitle: 'Bitte Option wählen',
        dismiss: false,
        options: (creditLimit.optionMessage?.options ?? []).map((option) => ({
          value: option,
          label: option,
        })),
      });
      canContinue =
        (isDefinedNotEmpty(chooseResult) && chooseResult !== 'DISMISSED') ||
        (creditLimit.optionMessage?.changeResult ?? false);
    }

    return {
      continue: canContinue,
      response: creditLimit,
    };
  }

  private async updateCartItem(
    key: string,
    changes: (item: CartItem) => UpdateCartItem
  ): Promise<Cart | null> {
    const cartId = this.cart()?.id;
    const cartItem = this.getCartItem(key);
    if (!cartItem || !cartId) return null;

    return await this.#track(
      this.#cartRepository.updateItem(cartId, cartItem.key, {
        ...changes(cartItem),
      })
    );
  }

  async finalize(body: any, addProcess = true): Promise<FinalizeResult | null> {
    const cartId = this.selectedProcess()?.id ?? this.#selectedProcess()?.id;
    if (!cartId) return null;

    // Credit-limit validation deliberately does NOT run on finalize — per Karli it
    // belongs on document-type and customer change only. Restore the two lines below
    // if that decision is reversed.
    // const creditLimitResult = await this.validateCreditLimit(undefined);
    // if (!creditLimitResult?.continue) return;

    const result = await this.#track(this.#cartRepository.finalize(cartId, body));

    if (addProcess) {
      const processes = await this.#cartRepository.loadProcesses();
      this.#processesResource.set(processes);
      // const newEmptyProccess = await this.addProcess();
      this.#selectedProcess.set(null); // switch back to the empty template process
    }

    return result;
  }

  getCartItem(key: string): CartItem | null {
    const cart = this.cart();
    if (!cart) return null;
    return cart.items.find((cartItem) => key === cartItem.key) ?? null;
  }

  async cancelCart(process?: Process): Promise<void> {
    const currentProcess = process ?? this.selectedProcess();
    if (!currentProcess) return;

    await this.removeProcess(currentProcess);
    const updatedProcesses = this.processes();

    if (updatedProcesses.length < 1) {
      this.selectProcess(null);
    } else {
      this.selectProcess(updatedProcesses[0]);
    }
  }

  readonly hasItems = computed(() => {
    const cart = this.cart();
    if (!cart) return false;
    return cart.items.length > 0 || cart.cashbookEntries.length > 0;
  });

  async transformDocumentType(
    id: number,
    address?: Address,
    copies?: number
  ): Promise<void> {
    await this.#track(this.#cartRepository.transformDocumentType(id, address, copies));
  }

  async createReturnCart(
    referenceDocument: ReferenceDocument,
    items: CartItem[]
  ): Promise<Cart | null> {
    const result = await this.#track(
      this.#cartRepository.createReturnCart({
        referenceDocument: referenceDocument,
        items: items,
      })
    );
    const processes = await this.#cartRepository.loadProcesses();
    this.#processesResource.set(processes);
    this.#selectedProcess.set({ id: result?.id, number: 0 } as Process);
    await this.setCart(result);
    return result;
  }

  async deleteAllCarts() {
    const carts = this.processes();
    for (const cart of carts) {
      try {
        await this.cancelCart(cart);
      } catch (e) {
        handleRequestError(e, this.injector, { show: true });
      }
    }
  }
}
